import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  useSeasonUpdate,
  SeasonUpdateInput,
  useSeasonCreate,
} from "@/graphql/hooks/useSeasonUpdate"
import { useAllGames } from "@/graphql/hooks/useGames"
import { useToast } from "@/hooks/use-toast"
import type { Season } from "@/graphql/types"
import { Ban, Check } from "lucide-react"

interface SeasonEditModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  season: Season | null
  onSuccess: () => void
  isNew: boolean
}

export function SeasonEditModal({
  open,
  onOpenChange,
  season,
  onSuccess,
  isNew,
}: SeasonEditModalProps) {
  const { updateSeason, loading: loadingUpdate } = useSeasonUpdate()
  const { createSeason, loading: loadingCreate } = useSeasonCreate()
  const { games } = useAllGames()
  const { toast } = useToast()
  const [formData, setFormData] = useState<SeasonUpdateInput>({
    id: 0,
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    isActive: false,
    isArchived: false,
    gameId: 0,
  })

  useEffect(() => {
    if (season) {
      setFormData({
        id: season.id,
        name: season.name,
        description: season.description || "",
        startDate: season.startDate.split("T")[0], // Format to YYYY-MM-DD
        endDate: season.endDate.split("T")[0], // Format to YYYY-MM-DD
        isActive: season.isActive === 1,
        gameId: season.gameId,
        isArchived: season.isArchived === 1,
      })
    }
  }, [season])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.gameId) {
      toast({
        title: "Error",
        description: "El juego es obligatorio, seleccionalo por favor.",
        variant: "destructive",
      })
    } else {
      try {
        if (isNew) {
          const result = await createSeason(formData)
          if (result?.status?.success) {
            toast({
              title: "Temporada creada",
              variant: "success",
              description: "La nueva temporada se ha creado correctamente",
            })
            onSuccess()
            onOpenChange(false)
          } else {
            throw new Error(result?.status?.detail || "Error desconocido")
          }
        } else {
          const result = await updateSeason(formData)
          if (result?.status?.success) {
            toast({
              title: "Temporada actualizada",
              variant: "success",
              description: "La temporada se ha actualizado correctamente",
            })
            onSuccess()
            onOpenChange(false)
          } else {
            throw new Error(result?.status?.detail || "Error desconocido")
          }
        }
      } catch (error) {
        toast({
          title: "Error",
          description:
            error instanceof Error
              ? error.message
              : `Error al ${isNew ? "crear" : "actualizar"} la temporada`,
          variant: "destructive",
        })
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[625px] bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">
            {isNew ? "Crear" : "Editar"} Temporada
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 dark:text-gray-400">
            {isNew ? "Crea" : "Modifica"} la información de la temporada
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nombre */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-gray-700 dark:text-gray-300">
              Nombre
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-gray-700 dark:text-gray-300">
              Descripción
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={3}
              className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Juego */}
          <div className="space-y-2">
            <Label htmlFor="gameId" className="text-gray-700 dark:text-gray-300">
              Juego
            </Label>
            <Select
              value={formData.gameId.toString()}
              onValueChange={(value) => setFormData({ ...formData, gameId: parseInt(value) })}
            >
              <SelectTrigger className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100">
                <SelectValue placeholder="Selecciona un juego" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                {games.map((game) => (
                  <SelectItem
                    key={game.id}
                    value={game.id.toString()}
                    className={game.isActive ? "" : "text-muted-foreground dark:text-gray-400"}
                  >
                    {game.name}
                    {game.isActive ? (
                      <Check className="h-3 w-3 mx-1 inline" />
                    ) : (
                      <Ban className="h-3 w-3 mx-1 inline" />
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate" className="text-gray-700 dark:text-gray-300">
                Fecha Inicio
              </Label>
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                required
                className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate" className="text-gray-700 dark:text-gray-300">
                Fecha Fin
              </Label>
              <Input
                id="endDate"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                required
                className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>

          {/* Switches */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
              <Label htmlFor="isActive" className="text-gray-700 dark:text-gray-300">
                Activa
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="isArchived"
                checked={formData.isArchived}
                onCheckedChange={(checked) => setFormData({ ...formData, isArchived: checked })}
              />
              <Label htmlFor="isArchived" className="text-gray-700 dark:text-gray-300">
                Archivada
              </Label>
            </div>
          </div>

          {/* Footer */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loadingUpdate || loadingCreate}>
              {loadingUpdate || loadingCreate ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
